/**
 * Message Detail Page (Admin)
 *
 * Shows full message details with:
 * - Message content
 * - Delivery statistics
 * - Per-recipient status
 */

import { useState, useEffect, FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getMessage, adminReplyToMessage, type Message, type MessageType, type DeliveryStatus } from '../../../api/messages';
import { getErrorMessage } from '../../../api/client';
import { CrewProfileLink } from '../../../components/crew';

const MESSAGE_TYPE_CONFIG: Record<MessageType, { label: string; className: string }> = {
  BROADCAST: { label: 'Direct Message', className: 'bg-blue-100 text-blue-800' },
  ASSIGNMENT: { label: 'Assignment', className: 'bg-purple-100 text-purple-800' },
  CREW_REPLY: { label: 'Crew Reply', className: 'bg-green-100 text-green-800' },
  EVENT_PUBLISHED: { label: 'New Event', className: 'bg-teal-100 text-teal-800' },
  EVENT_UPDATED: { label: 'Event Update', className: 'bg-amber-100 text-amber-800' },
};

const DELIVERY_STATUS_CONFIG: Record<DeliveryStatus, { label: string; className: string }> = {
  QUEUED: { label: 'Queued', className: 'bg-gray-100 text-gray-800' },
  SENT: { label: 'Sent', className: 'bg-blue-100 text-blue-800' },
  DELIVERED: { label: 'Delivered', className: 'bg-green-100 text-green-800' },
  FAILED: { label: 'Failed', className: 'bg-red-100 text-red-800' },
};

// Guard against status values this bundle doesn't know (e.g. a stale cached
// bundle after the API adds a status) — degrade to a plain badge, never crash.
const statusBadge = (status: DeliveryStatus) =>
  DELIVERY_STATUS_CONFIG[status] ?? { label: status, className: 'bg-gray-100 text-gray-800' };

export default function MessageDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [message, setMessage] = useState<Message | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Reply state
  const [showReply, setShowReply] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);

  const loadMessage = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const { message } = await getMessage(id);
      setMessage(message);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessage();
  }, [id]);

  const handleReply = async (e: FormEvent) => {
    e.preventDefault();
    if (!id || !replyBody.trim()) return;

    setSubmittingReply(true);
    setReplyError(null);

    try {
      await adminReplyToMessage(id, { body: replyBody.trim() });
      setReplyBody('');
      setShowReply(false);
      // Reload to show the new reply in the thread
      await loadMessage();
    } catch (err) {
      setReplyError(getErrorMessage(err));
    } finally {
      setSubmittingReply(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="space-y-4">
            <div className="h-6 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-32 bg-gray-200 rounded w-full"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !message) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">{error || 'Message not found'}</div>
        <Link to="/admin/messages" className="text-primary-600 hover:text-primary-500">
          Back to Messages
        </Link>
      </div>
    );
  }

  const typeConfig = MESSAGE_TYPE_CONFIG[message.messageType];
  const stats = message.deliveryStats;

  return (
    <div>
      {/* Back link */}
      <Link
        to="/admin/messages"
        className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Messages
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-gray-900">{message.subject}</h1>
            <span className={`px-2.5 py-0.5 text-xs font-medium ${typeConfig.className}`}>
              {typeConfig.label}
            </span>
          </div>
          <p className="text-gray-600">
            Sent by {message.sentBy.firstName} {message.sentBy.lastName} on {formatDate(message.sentAt)}
          </p>
          {message.event && (
            <p className="text-gray-500 text-sm mt-1">
              Event: <Link to={`/admin/events/${message.event.id}`} className="text-primary-600 hover:underline">{message.event.name}</Link>
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Message content */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Message Content</h2>
            <div className="prose prose-sm max-w-none">
              <p className="whitespace-pre-wrap text-gray-700">{message.body}</p>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex gap-2">
                <span className="text-sm text-gray-500">Channels:</span>
                {message.channels.includes('EMAIL') && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                    Email
                  </span>
                )}
                {message.channels.includes('SMS') && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                    SMS
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Replies thread */}
          {message.replies && message.replies.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Replies ({message.replies.length})
              </h2>
              <div className="space-y-4">
                {message.replies.map((reply) => {
                  const isCrewReply = reply.messageType === 'CREW_REPLY';
                  return (
                    <div
                      key={reply.id}
                      className={`border border-hairline border-l-[3px] bg-surface-panel p-4 ${isCrewReply ? 'border-l-preview' : 'border-l-rack'}`}
                    >
                      <div className="mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center text-xs font-semibold ${isCrewReply ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                            {reply.sentBy.firstName.charAt(0)}
                            {reply.sentBy.lastName.charAt(0)}
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {reply.sentBy.firstName} {reply.sentBy.lastName}
                          </span>
                          <span className={`px-2 py-0.5 text-xs font-medium ${isCrewReply ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                            {isCrewReply ? 'Crew' : 'Admin'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatDate(reply.sentAt)}
                        </p>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{reply.body}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Admin reply form */}
          {message.messageType !== 'ASSIGNMENT' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              {!showReply ? (
                <button
                  onClick={() => setShowReply(true)}
                  className="w-full py-2 text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  Reply to this thread
                </button>
              ) : (
                <form onSubmit={handleReply}>
                  <div className="mb-3">
                    <label htmlFor="admin-reply" className="block text-sm font-medium text-gray-700 mb-1">
                      Your Reply
                    </label>
                    <textarea
                      id="admin-reply"
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                      rows={4}
                      maxLength={5000}
                      placeholder="Type your reply..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-rack focus:border-rack resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Your reply will be visible to the original recipients in the app.
                    </p>
                  </div>

                  {replyError && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                      {replyError}
                    </div>
                  )}

                  <div className="flex gap-3 justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setShowReply(false);
                        setReplyBody('');
                        setReplyError(null);
                      }}
                      disabled={submittingReply}
                      className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submittingReply || !replyBody.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                      {submittingReply ? 'Sending...' : 'Send Reply'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Recipients table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Recipients ({message.recipients?.length || 0})
            </h2>
            {message.recipients && message.recipients.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Recipient
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email Status
                      </th>
                      {message.channels.includes('SMS') && (
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          SMS Status
                        </th>
                      )}
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Read
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {message.recipients.map((recipient) => {
                      const emailStatus = statusBadge(recipient.emailStatus);
                      const smsStatus = recipient.smsStatus ? statusBadge(recipient.smsStatus) : null;

                      return (
                        <tr key={recipient.id}>
                          <td className="px-4 py-3">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-semibold mr-3">
                                {recipient.user.firstName.charAt(0)}
                                {recipient.user.lastName.charAt(0)}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  <CrewProfileLink userId={recipient.user.id}>
                                    {recipient.user.firstName} {recipient.user.lastName}
                                  </CrewProfileLink>
                                </p>
                                <p className="text-xs text-gray-500">{recipient.user.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`px-2 py-0.5 text-xs font-medium ${emailStatus.className}`}>
                              {emailStatus.label}
                            </span>
                          </td>
                          {message.channels.includes('SMS') && (
                            <td className="px-4 py-3 whitespace-nowrap">
                              {smsStatus ? (
                                <span className={`px-2 py-0.5 text-xs font-medium ${smsStatus.className}`}>
                                  {smsStatus.label}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">N/A</span>
                              )}
                            </td>
                          )}
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {recipient.readAt ? formatDate(recipient.readAt) : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No recipients</p>
            )}
          </div>
        </div>

        {/* Sidebar - Delivery Stats */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Delivery Stats</h2>
            {stats ? (
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm text-gray-500">Total Recipients</dt>
                  <dd className="mt-1 text-2xl font-semibold text-gray-900">{stats.total}</dd>
                </div>

                {message.channels.includes('EMAIL') && (
                  <div className="pt-4 border-t border-gray-200">
                    <dt className="text-sm font-medium text-gray-700 mb-2">Email</dt>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Sent</span>
                        <span className="font-medium">{stats.emailSent}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Delivered</span>
                        <span className="font-medium text-green-600">{stats.emailDelivered}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Failed</span>
                        <span className="font-medium text-red-600">{stats.emailFailed}</span>
                      </div>
                    </div>
                  </div>
                )}

                {message.channels.includes('SMS') && (
                  <div className="pt-4 border-t border-gray-200">
                    <dt className="text-sm font-medium text-gray-700 mb-2">SMS</dt>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Sent</span>
                        <span className="font-medium">{stats.smsSent}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Delivered</span>
                        <span className="font-medium text-green-600">{stats.smsDelivered}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Failed</span>
                        <span className="font-medium text-red-600">{stats.smsFailed}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Read</span>
                    <span className="font-medium text-purple-600">{stats.read}</span>
                  </div>
                </div>
              </dl>
            ) : (
              <p className="text-sm text-gray-500">No statistics available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
